import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { useCallback, useEffect } from "react";

import { useAtom } from "../../app-jotai";
import { presentationAtom } from "../../data/workspace";

import "./Workspace.scss";

/**
 * Full-screen-ish presentation controller. Rendered as a child of <Excalidraw>.
 * Drives the viewport to each frame and handles keyboard navigation.
 */
export const PresentationOverlay = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const [presentation, setPresentation] = useAtom(presentationAtom);

  const goTo = useCallback(
    (index: number) => {
      if (!presentation) {
        return;
      }
      const clamped = Math.max(
        0,
        Math.min(index, presentation.frameIds.length - 1),
      );
      setPresentation({ ...presentation, index: clamped });
    },
    [presentation, setPresentation],
  );

  const exit = useCallback(() => setPresentation(null), [setPresentation]);

  // Drive the viewport to the current frame.
  useEffect(() => {
    if (!presentation || !excalidrawAPI) {
      return;
    }
    const frameId = presentation.frameIds[presentation.index];
    const frame = excalidrawAPI
      .getSceneElements()
      .find((el) => el.id === frameId);
    if (frame) {
      excalidrawAPI.setViewport({
        target: frame,
        fit: "contain",
        animation: true,
      });
    }
  }, [presentation, excalidrawAPI]);

  // Keyboard navigation.
  useEffect(() => {
    if (!presentation) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (["ArrowRight", "ArrowDown", " ", "PageDown"].includes(e.key)) {
        e.preventDefault();
        goTo(presentation.index + 1);
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        goTo(presentation.index - 1);
      } else if (e.key === "Escape") {
        e.preventDefault();
        exit();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKey, { capture: true });
  }, [presentation, goTo, exit]);

  if (!presentation) {
    return null;
  }

  const total = presentation.frameIds.length;
  const current = presentation.index;

  return (
    <div className="WorkspacePresentation">
      <div className="WorkspacePresentation__controls">
        <button
          type="button"
          onClick={() => goTo(current - 1)}
          disabled={current === 0}
          aria-label="Previous slide"
        >
          ‹
        </button>
        <span className="WorkspacePresentation__controls__counter">
          {current + 1} / {total}
        </span>
        <button
          type="button"
          onClick={() => goTo(current + 1)}
          disabled={current === total - 1}
          aria-label="Next slide"
        >
          ›
        </button>
        <button type="button" onClick={exit} aria-label="Exit presentation">
          ✕
        </button>
      </div>
    </div>
  );
};
